// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Shipment, ShipmentType } from '@/shipment/api/types';

// Estimated time per serialized asset for cascade updates (in milliseconds)
const MS_PER_ASSET = 50;

/**
 * Determines whether editing this shipment will trigger a cascade update
 * to linked asset event groups (AEGs).
 *
 * Cascade occurs when:
 * - Outbound shipments have been shipped (status is 'shipped' or 'delivered')
 * - Inbound shipments have been received (status is 'received')
 *
 * @param shipment - The shipment to check
 * @param shipmentType - Whether the shipment is inbound or outbound
 * @returns True if editing will cascade to AEGs
 */
export function hasAssetEventGroupCascade(
  shipment: Pick<Shipment, 'status'>,
  shipmentType: ShipmentType
): boolean {
  if (shipmentType === 'outbound') {
    return shipment.status === 'shipped' || shipment.status === 'delivered';
  }
  // Inbound shipments cascade when received
  return shipment.status === 'received';
}

/**
 * Calculates the total number of serialized tracked assets on a shipment
 * by summing the serialCount of all line items whose catalog item has assets.
 *
 * @param items - The shipment line items
 * @returns The total number of serialized assets
 */
export function getTotalSerializedAssetCount(items: Shipment['items']): number {
  return items.reduce((total, item) => {
    if (item.item.hasAssets) {
      return total + item.serialCount;
    }
    return total;
  }, 0);
}

/**
 * Calculates the estimated cascade update time in seconds based on the number
 * of serialized assets. Each asset takes approximately 50ms to update.
 *
 * @param serializedAssetCount - The total number of serialized assets
 * @returns The estimated time in seconds (rounded up)
 */
export function calculateCascadeTimeSeconds(
  serializedAssetCount: number
): number {
  if (serializedAssetCount <= 0) {
    return 0;
  }
  return Math.ceil((serializedAssetCount * MS_PER_ASSET) / 1000);
}
